import { HttpClient, HttpParams } from '@angular/common/http';
import { ChangeDetectorRef, inject, Injectable } from '@angular/core';
import { BehaviorSubject, catchError, Observable, of, Subscription, take, throwError } from 'rxjs';
import { DroitContrainteDto, ListeEmetteurDto, ListeNatureDroitDto } from '../models/droit-contraint.dto';
import { ZzzConfigService } from 'src/app/zzz/core/config/config.service';
import { EntityRecord, EntityStoreStrategy, EntityTableButton, EntityTableColumn, EntityTableColumnRenderer, EntityTableTemplate } from '@igo2/common/entity';
import * as olstyle from 'ol/style';
import olSourceCluster from 'ol/source/Cluster';
import olSourceVector from 'ol/source/Vector';
import {
  DataSource,
  DataSourceService, 
  Feature,  
  LayerService, 
  QueryableDataSourceOptions, 
  VectorLayer,
  GeoWorkspaceOptions,
  Layer,
  featureToOl,
  IgoMap,
  isLayerItem,
  createOlTooltipDrawAtPoint
} from '@igo2/geo';
import { LanguageService } from "@igo2/core/language";
import { MapState, ToolState, WorkspaceState } from "@igo2/integration";
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { PopUpRdeComponent } from 'src/app/integration/sigt-recherche-rde/pop-up-rde/pop-up-rde.component';
import { TransactionProprieteAmendementDto } from '../models/transaction-propriete-amendement.dto';
import { TransfertAuthoriteDto } from '../models/transfert-authorite.dto';

export type RdeContext = 
  | 'droitContraintes'
  | 'transactionsAmendements'
  | 'transfertAutorite';

@Injectable({
  providedIn: 'root'
})
export class RechercheRdeService {
  #mapState = inject(MapState);
  #dataSourceService = inject(DataSourceService);
  #layerService = inject(LayerService);
  languageService = inject(LanguageService);
  workspaceState = inject(WorkspaceState);
  dialog = inject(MatDialog);
  
  private urlApi: string;
  defaultStyle: olstyle.Style | ((feature: any, resolution: any) => olstyle.Style);
  dialogRef: MatDialogRef<PopUpRdeComponent>;
  isLayerItem = isLayerItem;
  public layers$ = new BehaviorSubject<Layer[]>([]);

  constructor(
    private httpClient: HttpClient,
    private configService: ZzzConfigService
  ) {
    this.urlApi = this.configService.get().authApiEndPoint;
    this.map.layerController.all$.subscribe(layers => {
      this.layers$.next(<Layer[]> layers.filter(l => this.isLayerItem(l) && String(l.id).startsWith('RDE')));
    });
  }

  get map(): IgoMap {
    return this.#mapState.map;
  }

  obtenirListeEmetteur(statut: string): Observable<ListeEmetteurDto[]> {
  const query = `Statut eq '${statut}'`;
  const params = new HttpParams().set('$filter', query);

  return this.httpClient
    .get<ListeEmetteurDto[]>(this.urlApi+"/RDE/Emetteurs", { params })
    .pipe(
    catchError(error => {
      console.error('Erreur lors de la récupération des émetteurs:', error);
      return throwError(() => new Error('Impossible de récupérer la liste des émetteurs.'));
    })
  );

  }

  obtenirListeNatureDroit(statut: string, emetteurs: string[]): Observable<ListeNatureDroitDto[]> {
    let query = `Statut eq '${statut}'`;
    if (emetteurs?.length > 0) {
      const emetteursStr = emetteurs.map(e => `'${e}'`).join(',');
      query = query + ` and Emetteurs in (${emetteursStr})`;
    }
    const params = new HttpParams().set('$filter', query);

    return this.httpClient
        .get<ListeNatureDroitDto[]>(this.urlApi+"/RDE/NatureDroits", { params })
        .pipe(
        catchError(error => {
          console.error('Erreur lors de la récupération des natures de droit:', error);
          return throwError(() => new Error('Impossible de récupérer la liste des natures de droit.'));
        })
      );

  }

  obtenirListeDroitsContraintes(
    statut:string,
    indicateurHistorique: boolean,
    indicateurConfidentiel: boolean,
    numeroDroitContrainte?: string,
    emetteurs?: string[],
    naturesDroit?: string[],
    typeDate?: string,
    dateDebut?: Date,
    dateFin?: Date
  ) : Observable<DroitContrainteDto[]> {
    let query = `Statut eq '${statut}' and IndicateurHistorique eq ${indicateurHistorique} and CodeConfidentiel eq ${indicateurConfidentiel}`;
    if (emetteurs?.length > 0) {
      const emetteursStr = emetteurs.map(e => `'${e}'`).join(',');
      query = query + ` and Emetteurs in (${emetteursStr})`;
    }
    if (naturesDroit?.length > 0) {
      const naturesStr = naturesDroit.map(n => `'${n}'`).join(',');
      query = query + ` and NatureDroit in (${naturesStr})`;
    }
    if(numeroDroitContrainte) {
      query = query +  ` and NoDroitContrainte eq '${numeroDroitContrainte}'`;
    }
    if(typeDate){
      if(dateDebut) {
        query = query + ` and ${typeDate} ge ${this.formatDate(dateDebut)}`
      }
      if (dateFin) {
        query = query + ` and ${typeDate} le ${this.formatDate(dateFin)}`
      } 
    }
    const params = new HttpParams().set('$filter', query);

    return this.httpClient
        .get<DroitContrainteDto[]>(this.urlApi+"/RDE/DroitsContraintes", { params })
        .pipe(
        catchError(error => {
          console.error('Erreur lors de la récupération des droits et contraintes:', error);
          return throwError(() => new Error('Impossible de récupérer la liste des droits et contraintes.'));
        })
      );
  }
  
  obtenirFicheDescriptive(row:any, historicalFlag:boolean = false) {
    if (row.hasOwnProperty("properties")) {
      const id = encodeURIComponent(row.properties["cleUnique"]);

      const requestUrl = this.urlApi+`/RDE/DroitsContraintes/${id}/FicheDescriptive`;
      const params = new HttpParams().set('historique', historicalFlag)
      params.set('confidendiel',false);

      return this.httpClient
          .get(requestUrl, { params: params, responseType: 'blob'} )
          .pipe(
          catchError(error => {
            console.error('Erreur lors de la récupération de la fiche descriptive', error);
            return throwError(() => new Error('Impossible de récupérer la fiche descriptive.'));
          })
        );
    }
  }
  obtenirHistorique(row:any): Observable<DroitContrainteDto[]> {
    if (row.hasOwnProperty("properties")) {
      const id = encodeURIComponent(row.properties["cleUnique"]);
      const statut = "AT";
      const indicateurHistorique = true;
      const indicateurConfidentiel = false;
      const numeroDroitContrainte = row.properties["Numéro"];
      let query = `Statut eq '${statut}' and IndicateurHistorique eq ${indicateurHistorique} and CodeConfidentiel eq ${indicateurConfidentiel}`;
      if(numeroDroitContrainte) {
        query = query +  ` and NoDroitContrainte eq '${numeroDroitContrainte}'`;
      }
      const params = new HttpParams().set('$filter', query);

      return this.httpClient
        .get<DroitContrainteDto[]>(this.urlApi+"/RDE/DroitsContraintes", { params })
        .pipe(
        catchError(error => {
          console.error('Erreur lors de la récupération des droits et contraintes:', error);
          return throwError(() => new Error('Impossible de récupérer la liste des droits et contraintes.'));
        })
      );
    }
  }
  obtenirListeTransactionsProprietesAmendements(
    statutTransaction:string,
    statutTraitement: string,
    indicateurHistorique: boolean,
    numero: string,
    dateDebut?: Date,
    dateFin?: Date
  ) : Observable<TransactionProprieteAmendementDto[]> {
    let query = `StatutTransaction eq '${statutTransaction}' and StatutTraitement eq ${statutTraitement} and IndicateurHistorique eq ${indicateurHistorique}`;
    if(numero) {
      query = query +  ` and Numero eq '${numero}'`;
    }

    if(dateDebut) {
      query = query + ` and DateRecherche ge ${this.formatDate(dateDebut)}`
    }
    if (dateFin) {
      query = query + ` and DateRecherche le ${this.formatDate(dateFin)}`
    } 

    const params = new HttpParams().set('$filter', query);

    return this.httpClient
        .get<TransactionProprieteAmendementDto[]>(this.urlApi+"/RDE/TransactionsProprietesAmendements", { params })
        .pipe(
        catchError(error => {
          console.error('Erreur lors de la récupération des transactions de propriétés et amendements:', error);
          return throwError(() => new Error('Impossible de récupérer la liste des transactions de propriétés et amendements.'));
        })
      );
  }
  obtenirHistoriqueTPA(row:any): Observable<TransactionProprieteAmendementDto[]> {
    if (row.hasOwnProperty("properties")) {
      const statutTransaction = "TR";
      const statutTraitement = "TM";
      const indicateurHistorique = true;
      const numeroTransaction = row.properties["cleUnique"];
      let query = `StatutTransaction eq '${statutTransaction}' and StatutTraitement eq ${statutTraitement} and IndicateurHistorique eq ${indicateurHistorique}`;
      if(numeroTransaction) {
        query = query +  ` and Numero eq '${numeroTransaction}'`;
      }
      const params = new HttpParams().set('$filter', query);

      return this.httpClient
        .get<TransactionProprieteAmendementDto[]>(this.urlApi+"/RDE/TransactionsProprietesAmendementsHistoriques", { params })
        .pipe(
        catchError(error => {
          console.error('Erreur lors de la récupération des transactions de propriété et amendements historiques:', error);
          return throwError(() => new Error('Impossible de récupérer la liste des transactions de propriété et amendements historiques.'));
        })
      );
    }
  }

  obtenirFicheDescriptiveTPA(row:any, historicalFlag:boolean = false) {
        if (row.hasOwnProperty("properties")) {
      const id = encodeURIComponent(row.properties["cleUnique"]);

      const requestUrl = this.urlApi+`/RDE/TransactionsProprietesAmendements/${id}/FicheDescriptive`;
      const params = new HttpParams().set('historique', historicalFlag)
      params.set('confidendiel',false);
      return this.httpClient
          .get(requestUrl, { params: params, responseType: 'blob'} )
          .pipe(
          catchError(error => {
            console.error('Erreur lors de la récupération de la fiche descriptive', error);
            return throwError(() => new Error('Impossible de récupérer la fiche descriptive.'));
          })
        );
    }
  }
  obtenirListeTransfertsAutorite(
    statut:string[],
    numero: number,
    dateDebut?: Date,
    dateFin?: Date
  ) : Observable<TransfertAuthoriteDto[]> {
    let query = `Statut in (${statut.map(s => `'${s}'`).join(',')})`;
    if(numero) {
      query = query +  ` and Numero eq '${numero}'`;
    }

    if(dateDebut) {
      query = query + ` and DateRecherche ge ${this.formatDate(dateDebut)}`
    }
    if (dateFin) {
      query = query + ` and DateRecherche le ${this.formatDate(dateFin)}`
    } 

    const params = new HttpParams().set('$filter', query);

    return this.httpClient
        .get<TransfertAuthoriteDto[]>(this.urlApi+"/RDE/TransfertsAutorite", { params })
        .pipe(
        catchError(error => {
          console.error("Erreur lors de la récupération des transferts d'autorité:", error);
          return throwError(() => new Error("Impossible de récupérer la liste des transferts d'autorité."));
        })
      );
  }

    obtenirFicheDescriptiveTA(row:any) {
        if (row.hasOwnProperty("properties")) {
      const id = encodeURIComponent(row.properties["cleUnique"]);

      const requestUrl = this.urlApi+`/RDE/TransfertsAutorite/${id}/FicheDescriptive`;
      return this.httpClient
          .get(requestUrl, { responseType: 'blob'} )
          .pipe(
          catchError(error => {
            console.error('Erreur lors de la récupération de la fiche descriptive', error);
            return throwError(() => new Error('Impossible de récupérer la fiche descriptive.'));
          })
        );
    }
  }
    addSearchResultsToMap(
      layerId: string, 
      features: Feature[], 
      cdRef: ChangeDetectorRef, 
      template: EntityTableTemplate, 
      style: olstyle.Style | ((feature, resolution) => olstyle.Style) = null):void {
      if (features.length) {
        if (this.map === undefined) {
          return;
        }       

        let nextCount = 1;
        const otherLayersOfSameType = this.map.layerController.all.filter(l => this.isLayerItem(l) && new RegExp(`^${layerId}_`).test(String(l.id)));
        if (otherLayersOfSameType.length > 0) {
          otherLayersOfSameType.sort((a, b) => {
            const aId = String(a.id);
            const bId = String(b.id);
            const aCount = parseInt(aId.substring(aId.lastIndexOf('_') + 1));
            const bCount = parseInt(bId.substring(bId.lastIndexOf('_') + 1));
            return aCount < bCount ? 1 : -1;
          });
          const lastId = String(otherLayersOfSameType[0].id);
          nextCount = parseInt(lastId.substring(lastId.lastIndexOf('_') + 1)) + 1;
        }
        if (nextCount > 1) {
          //show dialog
          this.dialogRef = this.dialog.open(PopUpRdeComponent, {
            width: "500px"
          });
          this.dialogRef.afterClosed().subscribe(result => {
            // if answer is yes create layer with current next count
            if (result) {
              this.createUniqueLayerWithFeatures(layerId, nextCount, features, cdRef, template, style);
            } else {
              // if answer is no delete last layer (nextCount - 1) & create layer with (nextCount -1)
              this.createUniqueLayerWithFeatures(layerId, nextCount - 1, features, cdRef, template, style);
            }
          });
        } else {
          // create layer with current next count
          this.createUniqueLayerWithFeatures(layerId, nextCount, features, cdRef, template, style);
        }
      }  
  }
  createUniqueLayerWithFeatures(
    layerId: string, 
    layerNumber: number, 
    features: Feature[], 
    cdRef: ChangeDetectorRef, 
    template: EntityTableTemplate, 
    style: olstyle.Style | ((feature, resolution) => olstyle.Style) = null) {

    
    // // Remove layer if it already exists
    this.map.layerController.all
      .filter(l => this.isLayerItem(l) && l.id === `${layerId}_${layerNumber}`)
      .map(l => this.map.layerController.remove(l));

    setTimeout(() => {
      this.#dataSourceService
        .createAsyncDataSource({
          type: 'vector',
          queryable: true
        } as QueryableDataSourceOptions)
        .pipe(take(1))
        .subscribe((dataSource: DataSource) => {
          let titreCoucheResultat =
            this.languageService.translate.instant('sigt.integration.rechercheRde.form.serviceRde.titreCoucheResultat');
          titreCoucheResultat = titreCoucheResultat.replace("{0}", features[0].meta.title).replace("{1}", layerNumber);

          const olLayer = this.#layerService.createLayer({
            id: `${layerId}_${layerNumber}`,
            isIgoInternalLayer: true,
            title: titreCoucheResultat,
            source: dataSource,
            visible: true,
            workspace: { enabled: true },
            style: style || this.defaultStyle,
            igoStyle: {
              editable: true
            }
          }) as VectorLayer;
          const featuresOl = features.map((f) => {
            return featureToOl(f, 'EPSG:3857');
          });

          const ol = dataSource.ol as olSourceVector | olSourceCluster;
          olLayer.options.workspace = Object.assign({}, olLayer.options.workspace, {
            queryOptions: { tabQuery: false }// to avoid showing the toastr-panel
          } as GeoWorkspaceOptions);
          ol.addFeatures(featuresOl);

          this.map.layerController.add(olLayer);
          cdRef.detectChanges();
          setTimeout(() => {
            this.selectionnerWorkspace(template, olLayer);
          },200);
        });
    }, 500);

  }
  removeLayer(layer: Layer) {
    this.map.layerController.remove(layer);
  }

  selectionnerWorkspace(template: EntityTableTemplate, layer: VectorLayer) {
    const layerId = layer.id;
    let workspaceStoreSubscriptionUntilActivated: Subscription = new Subscription();
    workspaceStoreSubscriptionUntilActivated =
      this.workspaceState.store.entities$.subscribe((workspaceEntries) => {
        const workspaceToActivate = workspaceEntries.find(w => w.id === layerId);
        if (workspaceToActivate) {
          workspaceToActivate.meta.tableTemplate.columns = template.columns;
          this.workspaceState.setActiveWorkspaceById(workspaceToActivate.id); 
        // remove default Fearure workspace actions that do not apply to features with no geometry.
          setTimeout(() => {
            if (!workspaceToActivate.actionStore.empty) {
              const actions  = workspaceToActivate.actionStore.all().filter(a => a.id === "zoomAuto" || a.id === "filterInMapExtent");
              workspaceToActivate.actionStore.deleteMany(actions);
            }
          }, 100);
        }
        workspaceStoreSubscriptionUntilActivated.unsubscribe();
      });
      const workspaceSelectionSubscription =
      this.workspaceState.workspaceSelection$.subscribe();
  }
  formatDate(date:Date):string{
    return date.toISOString();
  }
  getCleanKey(key: string, strict?:boolean): string {
      return this.languageService.translate.instant(`sigt.integration.rechercheRde.${key}`)
        .replaceAll("'", "’")
        .replaceAll(".", "");
  }
  getPropertyKeyAndTitle(context: RdeContext): { [key: string]: string } {
    const keysByContext: { [key in RdeContext]: string[] } = {
      droitContraintes: [
        'numero', 'dateMiseAJour', 'statut', 'categorie', 'natureDroit', 'type',
        'transaction'
      ],
      transactionsAmendements: [
        'numeroTransaction', 'dateInscription', 'dateModif', 'type', 'remarque',
        'acte'
      ],
      transfertAutorite: [
        'numero', 'datePriseEffet', 'dateInscription', 'beneficiaire'
      ]
    };

    const keys = keysByContext[context];
    const result: { [key: string]: string } = {};

    keys.forEach(key => {
      result[key] = this.getCleanKey(`form.${context}.${key}`);
    });

    return result;
  }

  getEntityTableTemplate(
      context: RdeContext,
      actionHandlers?: Record<string, (row: any, record?: EntityRecord<object>) => void>
    ): EntityTableTemplate {

    const propertyKeyAndTitle = this.getPropertyKeyAndTitle(context);

    const columnsByContext: { [key in RdeContext]: string[] } = {
      droitContraintes: [
        'numero', 'dateMiseAJour', 'statut', 'categorie', 'natureDroit', 'type',
        'transaction'
      ],
      transactionsAmendements: [
        'numeroTransaction', 'dateInscription', 'dateModif', 'type', 'remarque',
        'acte'
      ],
      transfertAutorite: [
        'numero', 'datePriseEffet', 'dateInscription', 'beneficiaire'
      ]
    };

    const columns:EntityTableColumn[] = columnsByContext[context].map(key => ({
      title: propertyKeyAndTitle[key],
      name: `properties.${propertyKeyAndTitle[key].replaceAll("’","")}`
    }));

    // Add details column
    columns.push({
      name: 'details',
      title: 'Détails',
      valueAccessor: (row: any, record?: EntityRecord<object>) => {
        const isSelected = !!record?.state?.selected;
        return [
          {
            icon: "description", //picture_as_pdf,
            color: 'accent',
            disabled: !isSelected,
            click: (row: any, record?: EntityRecord<object>) => {
              if (record?.state?.selected && actionHandlers["details"]) {
                actionHandlers["details"](row, record);
              }
            }
          }
        ] as EntityTableButton[];
      },
      renderer: EntityTableColumnRenderer.ButtonGroup
    });

     if (actionHandlers['getHistoricalData'] && (context === 'droitContraintes' || context === 'transactionsAmendements')){
    // Add Hitorical data column if needed
    columns.push({
      name: 'getHistoricalData',
      title: 'Historique',
      valueAccessor: (row: any, record?: EntityRecord<object>) => {
        const isSelected = !!record?.state?.selected;
        const hasHistory = this.hasHistory(context, row);
        return [
          {
            icon: hasHistory? "update": undefined,
            color: 'accent',
            disabled: !isSelected || !hasHistory,
            click: (row: any, record?: EntityRecord<object>) => {
              if (record?.state?.selected && actionHandlers['getHistoricalData']) {
                actionHandlers['getHistoricalData'](row, record);
              }
            }
          }
        ] as EntityTableButton[];
      },
      renderer: EntityTableColumnRenderer.ButtonGroup
    });
    }   
    return { columns };
  }

  hasHistory(context:RdeContext, row:any):boolean {
    switch(context){
      case 'droitContraintes': {
          const cleUnique = row.properties["cleUnique"];
          if (!cleUnique) return false;

          const parts = cleUnique.split(";");
          const noSequence = parseInt(parts[1], 10);

          return noSequence > 1;
      }
      case 'transactionsAmendements': {
          const numeroTransactionOrigine = row.properties["numeroTransactionOrigine"];
          return (!numeroTransactionOrigine)? false: true;
      }
      default:
        return false;
    }
  }

  formatFeaturesForTable(context: RdeContext, listeDto: DroitContrainteDto[]|TransactionProprieteAmendementDto[]|TransfertAuthoriteDto[], areHistorical?:boolean): Feature[] {
    const propertyKeyAndTitle = this.getPropertyKeyAndTitle(context);
    const title = this.getCleanKey(`layerTitles.${context}`);
    if (areHistorical) {
      const historicalContexts = {droitContraintes: "historiqueDroitContrainte", transactionsAmendements: "historiqueTransactionProprieteAmendement"};
      var titleHistory =  this.getCleanKey(`layerTitles.${historicalContexts[context]}`);
    }
    let i = 0;

    return listeDto.map(dto => {
      const baseProperties: { [key: string]: any } = {
        cleUnique: this.getUniqueKey(context, dto)
      };

      const contextSpecificProperties = this.getContextSpecificProperties(context, dto, propertyKeyAndTitle);

      return {
        type: "Feature",
        properties: {
          ...baseProperties,
          ...contextSpecificProperties
        },
        geometry: {
            type: "Point",
          //  coordinates: [-71.2082, 46.8139] //yqb
          coordinates: []
        },
        meta: {
          id: i++,
          title: this.getMetaTitle(context, dto, title, titleHistory, areHistorical)
        }
      } as Feature;
    });
  }


  private getContextSpecificProperties(
    context: RdeContext,
    dto: DroitContrainteDto|TransactionProprieteAmendementDto|TransfertAuthoriteDto,
    keys: { [key: string]: string }
  ): { [key: string]: any } {
    switch (context) {
      case 'droitContraintes':
        const dcDto = dto as DroitContrainteDto;
        return {
          [keys.numero]: dcDto.Numero,
          [keys.dateMiseAJour]: dcDto.DateMiseAJour ? (<any> dcDto.DateMiseAJour).split('T')[0] : null,
          [keys.statut]: dcDto.Statut,
          [keys.categorie]: `${dcDto.DescCategorie} (${dcDto.CodeCategorie})`,
          [keys.natureDroit]: `${dcDto.NatureDescription} (${dcDto.NatureTitre})`,
          [keys.type]: `${dcDto.DescCategorie} (${dcDto.CodeCategorie})`,
          [keys.transaction]: dcDto.Transaction
        };
      case 'transactionsAmendements':
        const tpaDto = dto as TransactionProprieteAmendementDto;
        return {
          ["numeroTransactionOrigine"]: tpaDto.NumeroTransactionOrigine,
          [keys.numeroTransaction.replaceAll("’","")]: (tpaDto.DatePresenceRegistreFoncier !== null)? tpaDto.NoInscriptionRegistreFoncier : tpaDto.NumeroTransaction,
          [keys.dateInscription]: tpaDto.DatePresenceRegistreFoncier ? (<any> tpaDto.DatePresenceRegistreFoncier).split('T')[0] : null,
          [keys.dateModif]: tpaDto.DateCreation ? (<any> tpaDto.DateCreation).split('T')[0] : null ,
          [keys.type]:  `${tpaDto.Type} (${tpaDto.DescriptionType})`,
          [keys.remarque]: tpaDto.Remarque,
          [keys.acte]: tpaDto.ActeNature
        };

      case 'transfertAutorite':
        const taDto = dto as TransfertAuthoriteDto;
        return {
          [keys.numero] : taDto.Numero,
          [keys.datePriseEffet.replaceAll("’","")] :  taDto.DatePrise ? (<any> taDto.DatePrise).split('T')[0] : null,
          [keys.dateInscription] :  taDto.DateInscription ? (<any> taDto.DateInscription).split('T')[0] : null,
          [keys.beneficiaire] : `${taDto.Beneficiaire} (${taDto.BeneficiaireAbrev})`
        };
     default:
        return {};
    }
  }
  private getUniqueKey(context:RdeContext, dto:DroitContrainteDto|TransactionProprieteAmendementDto|TransfertAuthoriteDto): string {
    switch(context) {
      case 'droitContraintes':
        return (dto as DroitContrainteDto).CleUnique;
      case 'transactionsAmendements':
        return (dto as TransactionProprieteAmendementDto).NumeroTransaction.toString();;
      case 'transfertAutorite':
        return (dto as TransfertAuthoriteDto).Numero.toString();
    }
  }
  private getMetaTitle(context:RdeContext, dto:any, title:string, titleHistory:string , areHistorical?:boolean): string {
    switch(context){
      case 'droitContraintes':
          if (areHistorical) {
            const key = dto.CleUnique;
            const parts = key.split(";"); 
            const titlePart0 = (parts[2] === "Droit")?"du droit":"de la contrainte";
            const titlePart1 = parts[0];
            return titleHistory.replace("{0}",titlePart0).replace("{1}",titlePart1);            
          } else {
            return title;
          }   
      case 'transactionsAmendements': 
            if (areHistorical) {
              return titleHistory.replace("{0}",dto.NumeroTransactionAffichage);
            } else{
              return title;
            }
      case 'transfertAutorite':
        return title;
    }
  }
  getStyle(context:RdeContext): olstyle.Style {
    switch(context) {
      case 'droitContraintes':
        return new olstyle.Style({
          stroke: new olstyle.Stroke({
            width: 1,
            color: [0, 0, 0, 1]
          }),
          fill: new olstyle.Fill({
            color: [56, 168, 0, 1]
          }),
          image: new olstyle.Circle({
            radius: 2,
            stroke: new olstyle.Stroke({
              width: 1,
              color: [0, 0, 0, 1]
            }),
            fill: new olstyle.Fill({
              color: [56, 168, 0, 1]
            })
          })
        });
      case 'transactionsAmendements':
      case 'transfertAutorite':
    }

    
  }
}
